import { computed, EventEmitter, Injectable, signal, TemplateRef, ViewContainerRef, WritableSignal } from "@angular/core";
import { SplitterComponent } from "@syncfusion/ej2-angular-layouts";
import { ExpandedSpaceDto } from "./workspace.component";
import { OpenDocInfoDto } from "../dto/open-doc-info-dto";
//import { FileSummaryDto } from '../dto/file-summary-dto';
import { Guid } from "guid-typescript";
import { TabState } from "../services/tabs.service";
import { DocDto } from "../dto/doc-dto";
import { ShellMessageService } from "../services/shell-message.service";


@Injectable({
  providedIn: "root",
})
export class ModuleSpacesService {
  private _extra: OpenDocInfoDto | null = null;
  public get extra(): OpenDocInfoDto | null {
    return this._extra;
  }
  public set extra(value: OpenDocInfoDto | null) {
    this._extra = value;
  }
  private openDocs: Array<OpenDocInfoDto | null> = []; // documenti aperti: openDocs[0] è aperto in module0 e openDocs[1] è aperto in module1, null se lo spazio è libero
  private openDocsOrdered: Array<OpenDocInfoDto | null> = [];

  private CustomTemplateModule0?: TemplateRef<any>;
  private CustomTemplateModule1?: TemplateRef<any>;
  private viewContainerModule0?: ViewContainerRef;
  private viewContainerModule1?: ViewContainerRef;

  private splitterObj?: SplitterComponent;

  constructor() {


  }

  setInitialState(state: TabState) {
    // Imposta lo stato inziale dei documenti
    this.openDocsOrdered = [...state.docs];
    this.extra = state.extra;
  }

  initializeSplitter(
    splitterObject: SplitterComponent,
    CustomTemplateModule0: TemplateRef<any>,
    viewContainerModule0: ViewContainerRef,
    CustomTemplateModule1: TemplateRef<any>,
    viewContainerModule1: ViewContainerRef
  ) {
    // Inizializza lo splitter e i vari spazi

    this.openDocs = [...this.openDocsOrdered];
    while (this.openDocs.length < 2) {
      this.openDocs.push(null);
    }

    this.splitterObj = splitterObject;

    this.CustomTemplateModule0 = CustomTemplateModule0;
    this.CustomTemplateModule1 = CustomTemplateModule1;
    this.viewContainerModule0 = viewContainerModule0;
    this.viewContainerModule1 = viewContainerModule1;

    if (this.openDocsOrdered.length >= 1) {
      this.addSpaceToSplitter(
        this.splitterObj,
        0,
        this.CustomTemplateModule0,
        this.viewContainerModule0,
        "module0"
      );
    }
    if (this.openDocsOrdered.length >= 2) {
      this.addSpaceToSplitter(
        this.splitterObj,
        1,
        this.CustomTemplateModule1,
        this.viewContainerModule1,
        "module1"
      );
    }
  }

  getOpenDocs(): Array<OpenDocInfoDto | null> {
    return this.openDocs;
  }

  closeAllDocs() {
    this.splitterObj?.removePane(1);
    this.splitterObj?.removePane(0);
    this.openDocs = [null, null];
    this.openDocsOrdered = [];
  }

  /**
   * Checks if a document with the specified ID is currently open.
   * 
   * @param docId - The document identifier to check against the open documents.
   * @returns True if the document is open, otherwise false.
   */

  getItemFromOpenDocs(docId: string) {
    return this.openDocs?.findIndex((d) => d?.id == docId) > -1;
  }

  getOpenDocsOrdered(): Array<OpenDocInfoDto | null> {
    return this.openDocsOrdered;
  }

  getExtra(): OpenDocInfoDto | null {
    return this.extra;
  }

  getExpandedModuleSpaces(): ExpandedSpaceDto[] {
    // Per ogni modulespace utilizzato, ritorna il valore dell'attributo expanded nella posizione che ha in ordered
    const expandedModuleSpaces: ExpandedSpaceDto[] = [];
    this.openDocsOrdered.forEach((doc) => {
      const space = this.openDocs.findIndex((d) => d?.id == doc?.id);
      if (space !== -1) {
        const targetChildElement: HTMLElement | null = document.getElementById(
          "module" + space
        );
        const targetElement = targetChildElement?.parentElement;
        if (this.splitterObj && targetElement) {
          expandedModuleSpaces.push({
            space: space,
            expanded: !targetElement.classList.contains("e-collapsed"),
          });
        }
      }
    });
    return expandedModuleSpaces;
  }

  removeDocByDocId(docId: string) {
    const docSpace = this.openDocs.findIndex((doc) => doc?.id == docId);
    // Rimuove un doc dallo splitter in base al suo id (attualmente nome file)
    if (docSpace != -1) {
      const splitterPos = this.getSplitterPositionByModuleSpace(docSpace);
      if (splitterPos != null) {
        this.splitterObj?.removePane(splitterPos);

        // Rimuovo doc dalle liste
        this.openDocs[docSpace] = null;
        const openDocsOrderedIdx = this.openDocsOrdered.findIndex(
          (doc) => doc?.id == docId
        );
        this.openDocsOrdered.splice(openDocsOrderedIdx, 1);
      }
    }
  }

  removeDocByDocSpace(docPos: number) {
    const doc = this.openDocsOrdered[docPos];
    const docSpace = this.openDocs.findIndex((d) => d?.id == doc?.id);
    const splitterPos = this.getSplitterPositionByModuleSpace(docSpace);
    if (splitterPos != null) {
      this.splitterObj?.removePane(splitterPos);
      // Rimuovo doc dalle liste
      this.openDocs[docSpace] = null;
      this.openDocsOrdered.splice(
        this.openDocsOrdered.findIndex((d) => d?.id == doc?.id),
        1
      );
    }
  }

  collapseByModulespace(moduleSpace: number) {
    const splitterPos = this.getSplitterPositionByModuleSpace(moduleSpace);
    if (splitterPos != null) {
      this.splitterObj?.collapse(splitterPos);
    }
  }

  expandByModulespace(moduleSpace: number) {
    const splitterPos = this.getSplitterPositionByModuleSpace(moduleSpace);
    if (splitterPos != null) {
      this.splitterObj?.expand(splitterPos);
    }
  }

  openDoc(doc: OpenDocInfoDto): number {
    // Get degli spazi liberi (module0 o module1) della prima posizione libera (non conosciamo l'ordinamento di module0 e module1)
    const freeSpaces: boolean[] = this.getAvailableModuleSpaces();
    const freePosition: number = this.getFirstFreePositionAvailable();
    let result = -1;
    // Se tutti gli spazi sono disponibili: apro in module0
    if (freeSpaces.filter((space) => space == true).length == 2) {
      this.openDocInModule0(freePosition, doc);
      result = 0;
    }
    // Se c'è un solo spazio disponibile: apro in quell spazio e alla prima posizione disponibile
    else if (freeSpaces.filter((space) => space == true).length == 1) {

      if (freeSpaces[0]) {
        this.openDocInModule0(freePosition, doc);
        result = 0;

      } else if (freeSpaces[1]) {
        this.openDocInModule1(freePosition, doc);
        result = 1;
      }
    } else {
      result = 1;
    }

    return result;
  }

  addSpaceToSplitter(
    splitterObject: SplitterComponent,
    idxSpace: number,
    template: TemplateRef<any>,
    viewContainer: ViewContainerRef,
    spacename: string
  ) {
    splitterObject.addPane(
      {
        content: "<ng-container id='" + spacename + "'></ng-container>",
        collapsible: false,
        size: "50%",
        min: "160px"
      },
      idxSpace
    );

    const templateValue = template.createEmbeddedView(null);
    viewContainer.clear();
    // inserts the template into the view container
    viewContainer.insert(templateValue);
    // Obtaining the content element of the panel
    const content = document.getElementById(spacename);
    // appending the template to the panel content element.
    content?.appendChild(templateValue.rootNodes[0]);
  }

  swapModuleSpaces(document: Document) {
    this.openDocsOrdered = this.openDocsOrdered.reverse();

    // Cerco il parent del module0
    const parentLeft = document.getElementById("module0")?.parentElement;
    const orderLeft = parentLeft?.style.order ?? null;

    // Cerco il parent del module1
    const parentRight = document.getElementById("module1")?.parentElement;
    const orderRight = parentRight?.style.order ?? null;

    parentLeft?.style.setProperty("order", orderRight);
    parentRight?.style.setProperty("order", orderLeft);
  }

  private getAvailableModuleSpaces(): boolean[] {
    return this.openDocs.map((doc) => doc == null);
  }

  private getFirstFreePositionAvailable(): number {
    // La prima posizione disponibile dipende da quanti documenti aperti
    return this.openDocsOrdered.length;
  }

  private generateModuleGuid(): string {
    return Guid.create().toString();
  }

  public generateOpenDocDto(doc: DocDto): OpenDocInfoDto {
    return {
      name: doc.name,
      type: doc.docType,
      id: doc.id,
      guid: this.generateModuleGuid(),
      isDirty: false,
      isShared: doc.isShared,
      saveStatus: 0,
      isInViewMode: doc.readonly,
      isInShowMode: false,
      isNewDoc: false,
      isBusy: false,
      hasChanged: false,
      isReadOnly: false,
      isLocked: false,
      startText: '',
      googleId: doc.googleId
    };
  }



  private openDocInModule0(position: number, doc: OpenDocInfoDto): void {
    if (
      this.splitterObj &&
      this.CustomTemplateModule0 &&
      this.viewContainerModule0
    ) {
      this.openDocs[0] = doc; // this.generateOpenDocDto(doc);
      if (position >= this.openDocsOrdered.length - 1) {
        this.openDocsOrdered.push(doc);
      } else {
        this.openDocsOrdered = [
          doc,
          // this.generateOpenDocDto(doc),
          ...this.openDocsOrdered,
        ];
      }
      this.addSpaceToSplitter(
        this.splitterObj,
        position,
        this.CustomTemplateModule0,
        this.viewContainerModule0,
        "module0"
      );
    }
  }

  private openDocInModule1(position: number, doc: OpenDocInfoDto): void {
    if (
      this.splitterObj &&
      this.CustomTemplateModule1 &&
      this.viewContainerModule1
    ) {
      this.openDocs[1] = doc;//this.generateOpenDocDto(doc);
      if (position >= this.openDocsOrdered.length - 1) {
        this.openDocsOrdered.push(doc);
      } else {
        this.openDocsOrdered = [
          doc,
          //this.generateOpenDocDto(doc),
          ...this.openDocsOrdered,
        ];
      }
      this.addSpaceToSplitter(
        this.splitterObj,
        position,
        this.CustomTemplateModule1,
        this.viewContainerModule1,
        "module1"
      );
    }
  }

  getSplitterPositionByModuleSpace(docSpace: number): number | null {
    // Trova indice nello splitter in base allo spazio che occupa (0: module0 e 1: module1)
    let idx: number | null = null;
    const targetChildElement: HTMLElement | null = document.getElementById(
      "module" + docSpace
    );
    const targetElement = targetChildElement?.parentElement;
    if (this.splitterObj && targetElement) {
      idx = Array.from(this.splitterObj?.element?.childNodes).indexOf(
        targetElement
      );
    }

    return idx;
  }

  setDocName(docId: string, docName: string) {
    const idx = this.openDocs.findIndex((doc: OpenDocInfoDto | null) => doc?.id === docId);
    if (idx > -1 && this.openDocs && this.openDocs[idx]) {
      this.openDocs[idx].name = docName;
    }
  }

  getDocName(docId: string): string {
    return this.openDocs?.find((doc: OpenDocInfoDto | null) => doc?.id === docId)?.name || "";
  }

}
